/**
 * Google Analytics 4 helper — Emad Platform
 *
 * Two GA4 properties are configured in public/index.html:
 *   - G-QLBQ3FTNDH
 *   - G-QV6XGBFXKV
 * Every `gtag('event', ...)` call fans out to both properties automatically.
 *
 * PII policy: never pass raw email / name / phone. Prefer role, id-less
 * counters, timestamps, and UTM. Registrations only send hashed length or
 * role. See utm.js for the sanctioned attribution payload.
 */

const HAS_GTAG = () =>
  typeof window !== "undefined" && typeof window.gtag === "function";

const META_PIXEL_ID = (process.env.REACT_APP_META_PIXEL_ID || "").trim();

export function initMetaPixel() {
  if (typeof window === "undefined" || typeof document === "undefined" || !META_PIXEL_ID) return false;
  if (window.__emad_meta_pixel_id__ === META_PIXEL_ID && typeof window.fbq === "function") return true;
  if (window.__emad_meta_pixel_id__ && window.__emad_meta_pixel_id__ !== META_PIXEL_ID) return false;

  const shouldLoadScript = typeof window.fbq !== "function";
  if (shouldLoadScript) {
    const fbq = function (...args) {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;
  }

  if (
    shouldLoadScript
    && !document.getElementById("emad-meta-pixel")
    && !document.querySelector("script[src*=\"connect.facebook.net\"]")
  ) {
    const script = document.createElement("script");
    script.id = "emad-meta-pixel";
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  window.fbq("init", META_PIXEL_ID);
  window.__emad_meta_pixel_id__ = META_PIXEL_ID;
  return true;
}

const HAS_FBQ = () => initMetaPixel() && typeof window.fbq === "function";

/**
 * Fire a Meta Pixel event. Standard events (PageView, ViewContent, Lead,
 * CompleteRegistration, Purchase, etc.) go through `fbq('track', ...)`;
 * anything custom is routed to `fbq('trackCustom', ...)`.
 */
const META_STANDARD_EVENTS = new Set([
  "PageView", "ViewContent", "Lead", "CompleteRegistration", "Contact",
  "Search", "AddToCart", "InitiateCheckout", "Purchase", "Subscribe",
  "StartTrial", "Schedule", "SubmitApplication",
]);

export function trackPixel(name, params = {}) {
  if (!name || !HAS_FBQ()) return;
  try {
    const method = META_STANDARD_EVENTS.has(name) ? "track" : "trackCustom";
    window.fbq(method, name, params);
  } catch {
    /* noop */
  }
}

/**
 * Track a custom event across both GA4 properties + PostHog (if present).
 * Safe when the library did not load (ad-blocker, offline) — becomes a no-op.
 */
export function trackEvent(name, params = {}) {
  if (!name) return;
  try {
    if (HAS_GTAG()) {
      window.gtag("event", name, params);
    }
    if (typeof window !== "undefined" && window.posthog?.capture) {
      window.posthog.capture(name, params);
    }
  } catch {
    /* noop */
  }
}

/**
 * SPA-safe page_view. Should fire on every react-router location change.
 */
export function trackPageView(pathWithQuery, title) {
  if (!HAS_GTAG()) return;
  try {
    window.gtag("event", "page_view", {
      page_path: pathWithQuery,
      page_title: title || (typeof document !== "undefined" ? document.title : ""),
      page_location:
        typeof window !== "undefined" ? window.location.href : undefined,
    });
  } catch {
    /* noop */
  }
}

/**
 * Set a stable user identifier once we know the role (never the email/name).
 * GA4 requires a hashed string. We only pass the role for segmentation.
 */
export function identifyRole(role) {
  if (!role || !HAS_GTAG()) return;
  try {
    window.gtag("set", "user_properties", { app_role: role });
  } catch {
    /* noop */
  }
}
